<template>
  <div
    class="text-field"
    :class="{
      focused: isFocused,
      filled: Boolean(inputValue),
    }"
  >
    <span class="field-icon">
      <svg
        v-if="iconType === 'email'"
        width="19"
        height="19"
        viewBox="0 0 19 19"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="2.2"
          y="4"
          width="14.6"
          height="11"
          rx="2.4"
          stroke="currentColor"
          stroke-width="1.4"
        />

        <path
          d="M3.5 5.7L9.5 10.2L15.5 5.7"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>

      <svg
        v-else-if="iconType === 'password'"
        width="19"
        height="19"
        viewBox="0 0 19 19"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="3"
          y="8"
          width="13"
          height="8.2"
          rx="2.5"
          stroke="currentColor"
          stroke-width="1.4"
        />

        <path
          d="M6.2 8V6.2C6.2 4.3 7.7 2.8 9.5 2.8C11.3 2.8 12.8 4.3 12.8 6.2V8"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
        />

        <circle
          cx="9.5"
          cy="12"
          r="1.1"
          fill="currentColor"
        />
      </svg>

      <svg
        v-else-if="iconType === 'phone'"
        width="19"
        height="19"
        viewBox="0 0 19 19"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="5.1"
          y="1.8"
          width="8.8"
          height="15.4"
          rx="2.4"
          stroke="currentColor"
          stroke-width="1.4"
        />

        <path
          d="M8 4H11M8.7 14.6H10.3"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
        />
      </svg>

      <svg
        v-else
        width="19"
        height="19"
        viewBox="0 0 19 19"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="9.5"
          cy="6"
          r="3.1"
          stroke="currentColor"
          stroke-width="1.4"
        />

        <path
          d="M3.8 16C4.4 12.9 6.4 11.3 9.5 11.3C12.6 11.3 14.6 12.9 15.2 16"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
        />
      </svg>
    </span>

    <input
      :id="fieldId"
      v-model="inputValue"
      :type="type"
      v-bind="$attrs"
      class="input-field"
      autocomplete="off"
      @focus="isFocused = true"
      @blur="handleBlur"
    />

    <label
      :for="fieldId"
      class="input-label"
      :class="{
        active: isFocused || inputValue,
      }"
    >
      {{ placeholder }}
    </label>

    <span class="focus-glow"></span>
  </div>
</template>

<script setup>
import {
  computed,
  ref,
  useId,
  watch,
} from "vue";

const props = defineProps({
  placeholder: {
    type: String,
    default: "",
  },

  modelValue: {
    type: String,
    default: "",
  },

  type: {
    type: String,
    default: "text",
  },

  icon: {
    type: String,
    default: "",
  },
});

const emit = defineEmits([
  "update:modelValue",
]);

const inputValue = ref(
  props.modelValue || ""
);

const isFocused = ref(false);

const generatedId = useId();

const fieldId = computed(() => {
  return `input-${generatedId}`;
});

const iconType = computed(() => {
  if (props.icon) {
    return props.icon;
  }

  const normalizedPlaceholder =
    props.placeholder.toLowerCase();

  if (
    props.type === "email" ||
    normalizedPlaceholder.includes("email") ||
    normalizedPlaceholder.includes("почт")
  ) {
    return "email";
  }

  if (
    props.type === "password" ||
    normalizedPlaceholder.includes("парол")
  ) {
    return "password";
  }

  if (
    props.type === "tel" ||
    normalizedPlaceholder.includes("телефон")
  ) {
    return "phone";
  }

  return "user";
});

watch(
  () => props.modelValue,
  (newValue) => {
    inputValue.value = newValue || "";
  }
);

watch(
  inputValue,
  (newValue) => {
    if (newValue !== props.modelValue) {
      emit(
        "update:modelValue",
        newValue
      );
    }
  }
);

const handleBlur = () => {
  isFocused.value = false;
};
</script>

<style scoped>
.text-field {
  position: relative;

  width: 330px;
  height: 50px;

  display: flex;
  align-items: center;

  box-sizing: border-box;

  overflow: hidden;

  padding: 0 15px 0 47px;

  border:
    1px solid
    var(--field-border);

  border-radius: 10px;

  background:
    linear-gradient(
      180deg,
      var(--field-bg-top) 0%,
      var(--field-bg-bottom) 100%
    );

  box-shadow:
    inset 0 1px 0
    var(--field-highlight),
    0 4px 12px
    rgba(0, 0, 0, 0.05);

  transition:
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    background 0.22s ease,
    transform 0.22s ease;
}

.text-field:hover {
  border-color:
    var(--field-border-hover);
}

.text-field.focused {
  border-color:
    rgba(40, 135, 247, 0.65);

  background:
    linear-gradient(
      180deg,
      var(--field-focus-top) 0%,
      var(--field-focus-bottom) 100%
    );

  box-shadow:
    inset 0 1px 0
    var(--field-highlight),
    0 0 0 4px
    rgba(40, 135, 247, 0.1),
    0 8px 20px
    rgba(33, 115, 229, 0.1);
}

.field-icon {
  position: absolute;
  z-index: 2;

  left: 14px;
  top: 50%;

  width: 24px;
  height: 24px;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    var(--field-icon);

  transform:
    translateY(-50%);

  transition:
    color 0.22s ease,
    transform 0.22s ease;
}

.text-field.focused .field-icon {
  color: #2887f7;

  transform:
    translateY(-50%)
    scale(1.05);
}

.input-field {
  position: relative;
  z-index: 2;

  width: 100%;
  height: 100%;

  box-sizing: border-box;

  padding: 14px 0 0;

  border: none;
  outline: none;

  font-family:
    "Eastman-Light",
    sans-serif;
  font-size: 14px;

  color:
    var(--field-text);

  background: transparent;

  caret-color: #2887f7;
}

.input-label {
  position: absolute;
  z-index: 2;

  left: 47px;
  top: 50%;

  max-width:
    calc(100% - 62px);

  overflow: hidden;

  font-family:
    "Eastman-Light",
    sans-serif;
  font-size: 14px;
  line-height: 1;
  white-space: nowrap;
  text-overflow: ellipsis;

  color:
    var(--label-color);

  pointer-events: none;

  transform:
    translateY(-50%);

  transform-origin:
    left center;

  transition:
    top 0.22s ease,
    color 0.22s ease,
    font-size 0.22s ease,
    transform 0.22s ease;
}

.input-label.active {
  top: 12px;

  font-size: 9px;

  color:
    var(--label-active);

  transform:
    translateY(0);
}

.text-field.focused .input-label.active {
  color: #2887f7;
}

.focus-glow {
  position: absolute;

  top: -40px;
  right: -55px;

  width: 110px;
  height: 110px;

  border-radius: 50%;

  background:
    rgba(40, 135, 247, 0.15);

  filter: blur(34px);

  opacity: 0;

  pointer-events: none;

  transition:
    opacity 0.25s ease;
}

.text-field.focused .focus-glow {
  opacity: 1;
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  transition:
    background-color 5000s
    ease-in-out 0s;

  -webkit-text-fill-color:
    var(--field-text) !important;

  box-shadow:
    0 0 0 1000px
    transparent inset !important;

  font-family:
    "Eastman-Regular",
    sans-serif !important;

  caret-color:
    var(--field-text);
}

@media (prefers-reduced-motion: reduce) {
  .text-field,
  .field-icon,
  .input-label,
  .focus-glow {
    transition-duration:
      0.01ms !important;
  }
}
</style>

<style>
:root {
  --field-bg-top: #4a4a4a;
  --field-bg-bottom: #444444;

  --field-focus-top: #4a4c50;
  --field-focus-bottom: #42454a;

  --field-text: #ffffff;

  --field-border:
    rgba(255, 255, 255, 0.05);

  --field-border-hover:
    rgba(255, 255, 255, 0.11);

  --field-highlight:
    rgba(255, 255, 255, 0.06);

  --field-icon:
    rgba(255, 255, 255, 0.4);

  --label-color:
    #c0c0c0;

  --label-active:
    #929292;
}

[data-theme="light"] {
  --field-bg-top: #eeeeee;
  --field-bg-bottom: #e8e8e8;

  --field-focus-top: #f3f7fd;
  --field-focus-bottom: #edf3fb;

  --field-text: #111111;

  --field-border:
    rgba(0, 0, 0, 0.05);

  --field-border-hover:
    rgba(0, 0, 0, 0.11);

  --field-highlight:
    rgba(255, 255, 255, 0.75);

  --field-icon:
    rgba(17, 17, 17, 0.42);

  --label-color:
    #888888;

  --label-active:
    #666666;
}
</style>